/**
 * Database utilities for direct data access
 */
import { supabase } from '../lib/supabaseClient';

// SECURITY 2026-05-21: estas constantes SUPABASE_URL y SERVICE_ROLE_KEY
// estaban hardcoded en este archivo. El módulo no las usaba (dead code),
// pero el SERVICE_ROLE key TERMINABA EN EL BUNDLE JS público — cualquier
// visitante con DevTools tenía acceso admin total a la DB. Removido.
//
// Para cualquier operación que efectivamente requiera service_role,
// usar `supabase` importado arriba (que es el cliente anon-backed) y
// validar permisos vía RLS / RPCs SECURITY DEFINER. Operaciones admin
// genuinas deben pasar por un backend Vercel API route.

// Add a cache for approval status to persist data during connection issues
const approvalStatusCache = new Map<string, any>();

// Track connection status
let supabaseConnected = true;
let lastConnectionAttempt = 0;
const CONNECTION_RETRY_INTERVAL = 30000; // 30 seconds

// Helper function to parse SQL query
export const parseQuery = (query: string): { table: string; type: string; conditions: any } => {
  let table = '';
  let type = '';
  const conditions: Record<string, any> = {};
  
  try {
    // Normalize the query (remove excess whitespace and make lowercase for easier parsing)
    const normalizedQuery = query.trim();
    const lowerQuery = normalizedQuery.toLowerCase();
    
    // Determine the query type
    if (lowerQuery.startsWith('select')) {
      type = 'select';
      
      const fromMatch = lowerQuery.match(/from\s+([a-z0-9_"]+)/i);
      if (fromMatch && fromMatch[1]) {
        table = fromMatch[1].replace(/"/g, '');
      }
      
      // Try to extract conditions (e.g., WHERE clauses)
      const whereMatch = lowerQuery.match(/where\s+(.+?)(?:order by|group by|limit|$)/i);
      if (whereMatch && whereMatch[1]) {
        const whereClause = whereMatch[1].trim();
        const conditionParts = whereClause.split(/\s+and\s+/i);
        
        conditionParts.forEach(condition => {
          // Parse basic equality conditions like "column = 'value'"
          const parts = condition.match(/([a-z0-9_]+)\s*=\s*['"]?([^'"\s]+)['"]?/i);
          if (parts && parts.length >= 3) {
            conditions[parts[1]] = parts[2].replace(/['"]/g, '');
          }
        });
      }
    } else if (lowerQuery.startsWith('insert')) {
      type = 'insert';
      
      // Extract table name from insert statement - use the original case from the query
      const intoMatch = normalizedQuery.match(/INTO\s+([a-zA-Z0-9_"]+)/i);
      if (intoMatch && intoMatch[1]) {
        table = intoMatch[1].replace(/"/g, '');
      }
    } else if (lowerQuery.startsWith('update')) {
      type = 'update';
      
      // Extract table name from update statement
      const updateMatch = normalizedQuery.match(/UPDATE\s+([a-zA-Z0-9_"]+)/i);
      if (updateMatch && updateMatch[1]) {
        table = updateMatch[1].replace(/"/g, '');
      }
      
      // Try to extract conditions for updates
      const whereMatch = lowerQuery.match(/where\s+(.+?)(?:returning|$)/i);
      if (whereMatch && whereMatch[1]) {
        const whereClause = whereMatch[1].trim();
        const conditionParts = whereClause.split(/\s+and\s+/i);
        
        conditionParts.forEach(condition => {
          const parts = condition.match(/([a-z0-9_]+)\s*=\s*['"]?([^'"\s]+)['"]?/i);
          if (parts && parts.length >= 3) {
            conditions[parts[1]] = parts[2].replace(/['"]/g, '');
          }
        });
      }
    } else if (lowerQuery.startsWith('delete')) {
      type = 'delete';
      
      // Extract table name from delete statement
      const fromMatch = lowerQuery.match(/from\s+([a-z0-9_"]+)/i);
      if (fromMatch && fromMatch[1]) {
        table = fromMatch[1].replace(/"/g, '');
      }
    } else {
      // For other statements, use a more generic approach
      type = 'other';
      
      // Try to find any table reference
      const tableMatch = lowerQuery.match(/(?:from|into|update|join)\s+([a-z0-9_"]+)/i);
      if (tableMatch && tableMatch[1]) {
        table = tableMatch[1].replace(/"/g, '');
      }
    }
  } catch (e) {
    console.error('Error parsing SQL query:', e);
  }
  
  return { table, type, conditions };
};

// Execute query specifically with Supabase
export const executeSupabaseQuery = async (
  table: string, 
  type: string, 
  conditions: any, 
  originalQuery: string
): Promise<any[]> => {
  if (!table) {
    throw new Error('Table name is required for Supabase query execution');
  }
  
  try {
    let result: any[] = [];
    
    if (type === 'select') {
      // For select queries, use Supabase query builder
      let query = supabase.from(table).select('*');
      
      // Apply conditions if available
      if (conditions && Object.keys(conditions).length > 0) {
        Object.entries(conditions).forEach(([key, value]) => {
          query = query.eq(key, value);
        });
      }
      
      const { data, error } = await query;
      
      if (error) throw error;
      result = data || [];
    } else if (type === 'insert') {
      // For INSERT queries, first try to use the MCP server as it's more reliable
      try {
        console.log('Attempting to execute INSERT query via MCP server...');
        result = await executeMcpQuery(originalQuery);
        console.log('MCP server INSERT successful:', result);
        return result;
      } catch (mcpError) {
        console.error('MCP server error for INSERT query, falling back to Supabase client:', mcpError);
        
        // Extract data from the SQL INSERT VALUES clause
        const fieldsMatch = originalQuery.match(/\(([^)]+)\)\s+VALUES/i);
        const valuesMatch = originalQuery.match(/VALUES\s*\(([^)]+)\)/i);
        
        if (fieldsMatch && valuesMatch) {
          const fields = fieldsMatch[1].split(',').map(f => f.trim());
          
          // Handle quoted values and commas in strings properly
          const valueString = valuesMatch[1];
          const values: string[] = [];
          let currentValue = '';
          let inQuote = false;
          
          for (let i = 0; i < valueString.length; i++) {
            const char = valueString[i];
            
            if (char === "'" && (i === 0 || valueString[i-1] !== '\\')) {
              inQuote = !inQuote;
              currentValue += char;
            } 
            else if (char === ',' && !inQuote) {
              values.push(currentValue.trim());
              currentValue = '';
            } 
            else {
              currentValue += char;
            }
          }
          
          // Add the last value
          if (currentValue.trim()) {
            values.push(currentValue.trim());
          }
          
          // Create an object for insertion
          const insertData: Record<string, any> = {};
          
          fields.forEach((field, i) => {
            if (i < values.length) {
              let value: any = values[i];
              
              // Remove quotes for string values
              if (value.startsWith("'") && value.endsWith("'")) {
                value = value.substring(1, value.length - 1);
              } 
              // Handle NULL values
              else if (value.toUpperCase() === 'NULL') {
                value = null;
              }
              // Convert booleans
              else if (value.toLowerCase() === 'true') {
                value = true;
              }
              else if (value.toLowerCase() === 'false') {
                value = false;
              }
              // Convert numbers
              else if (!isNaN(Number(value)) && value !== '') {
                value = Number(value);
              }
              
              // Skip 'product_price' field when inserting into applications table
              // This fixes the error "Could not find the 'product_price' column of 'applications'"
              if (table === 'applications' && field.trim() === 'product_price') {
                console.log(`Skipping product_price field for applications table as it doesn't exist in schema`);
              } 
              // Ensure proper values for status (always 'solicitud') and application_type (always 'selected_plans')
              else if (table === 'applications' && field.trim() === 'status') {
                insertData[field] = 'solicitud';
              }
              else if (table === 'applications' && field.trim() === 'application_type') {
                insertData[field] = 'selected_plans';
              }
              else {
                insertData[field] = value;
              }
            }
          });
          
          console.log('Inserting data directly with Supabase client:', insertData);
          
          // Use Supabase client to insert the data
          const { data, error } = await supabase.from(table).insert([insertData]).select();
          
          if (error) {
            console.error('Supabase insert error:', error);
            throw error;
          }
          
          console.log('Supabase insert successful:', data);
          result = data || [];
        } else {
          console.error('Could not parse INSERT query:', originalQuery);
          throw new Error('Could not parse INSERT query for Supabase client execution');
        }
      }
    } else if (type === 'update' || type === 'delete') {
      // For UPDATE and DELETE queries, always use MCP server first
      console.log(`Executing ${type.toUpperCase()} query via MCP server...`);
      try {
        result = await executeMcpQuery(originalQuery);
        console.log(`MCP server ${type.toUpperCase()} successful:`, result);
      } catch (mcpError) {
        console.error(`MCP server error for ${type.toUpperCase()} query:`, mcpError);
        throw mcpError;
      }
    } else {
      // For all other query types, use MCP server
      try {
        result = await executeMcpQuery(originalQuery);
      } catch (mcpError) {
        console.error('MCP server error for query:', mcpError);
        throw mcpError;
      }
    }
    
    return result;
  } catch (error) {
    console.error('Supabase query execution error:', error);
    throw error;
  }
};

// Execute query with MCP server
export const executeMcpQuery = async (query: string): Promise<any[]> => {
  try {
    // Use the correct MCP server URL
    const response = await fetch('http://localhost:3100/query', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ query }),
      // Add a timeout to avoid hanging
      signal: AbortSignal.timeout(10000) // 10 second timeout
    });

    if (!response.ok) {
      console.error(`MCP server error: ${response.status} ${response.statusText}`);
      // If we get a 404, the server might not be running
      if (response.status === 404) {
        console.error('MCP server returned 404. Make sure the server is running on port 3100');
      }
      throw new Error(`MCP server responded with status: ${response.status}`);
    }

    const result = await response.json();
    console.log('MCP server response:', result);
    return result.data || [];
  } catch (error) {
    console.error('MCP server connection error:', error);
    // If it's an INSERT query, provide a fallback with a UUID
    if (query.toLowerCase().startsWith('insert')) {
      console.warn('Creating fallback response for INSERT query');
      const uuid = crypto.randomUUID ? crypto.randomUUID() : Date.now().toString(36) + Math.random().toString(36).substring(2);
      return [{
        id: uuid,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      }];
    }
    throw error;
  }
};

// Helper function to check and potentially reconnect to Supabase
export const ensureSupabaseConnection = async (): Promise<boolean> => {
  const now = Date.now();
  
  // Only retry connection every 30 seconds to avoid too many reconnect attempts
  if (!supabaseConnected && (now - lastConnectionAttempt) > CONNECTION_RETRY_INTERVAL) {
    console.log('Attempting to reconnect to Supabase...');
    lastConnectionAttempt = now;
    
    try {
      // Simple health check query
      const { data, error } = await supabase.from('applications').select('count(*)', { count: 'exact' }).limit(1);
      
      if (error) {
        console.error('Supabase reconnection failed:', error);
        supabaseConnected = false;
        return false;
      }
      
      console.log('Successfully reconnected to Supabase');
      supabaseConnected = true;
      return true;
    } catch (error) {
      console.error('Supabase reconnection error:', error);
      supabaseConnected = false;
      return false;
    }
  }
  
  return supabaseConnected;
};

// Function to execute a database query
export const executeQuery = async (query: string): Promise<any[]> => {
  // Log the query for debugging
  console.log('Original SQL query:', query);
  
  // Try to ensure we have a connection first if we're not connected
  if (!supabaseConnected) {
    await ensureSupabaseConnection();
  }
  
  try {
    // First, try to execute with Supabase
    const { table, type, conditions } = parseQuery(query);
    
    if (!table) {
      console.error('Could not determine table from query:', query);
      throw new Error('Invalid query: Could not determine table');
    }
    
    console.log('Extracted table name:', `'${table}'`);
    
    // For approval status queries, check the cache first in case of connection issues
    if (table === 'applications' && query.includes('approved_by_advisor') && query.includes('WHERE id =')) {
      // Extract the application ID from the query
      const idMatch = query.match(/WHERE id = '([^']+)'/);
      if (idMatch && idMatch[1]) {
        const appId = idMatch[1];
        
        try {
          // Attempt to execute the query normally
          const result = await executeSupabaseQuery(table, type, conditions, query);
          
          // If successful, update the cache
          if (result && result.length > 0) {
            approvalStatusCache.set(appId, result[0]);
            console.log(`Updated approval cache for ${appId}`);
          }
          
          return result;
        } catch (error) {
          // If query fails but we have cached data, use it
          if (approvalStatusCache.has(appId)) {
            console.log(`Using cached approval data for ${appId} due to connection error`);
            return [approvalStatusCache.get(appId)];
          }
          // Otherwise, rethrow the error
          throw error;
        }
      }
    }
    
    // Execute the query through Supabase
    const result = await executeSupabaseQuery(table, type, conditions, query);
    
    // If we get here, connection is working
    supabaseConnected = true;
    
    return result;
  } catch (error) {
    // Mark connection as potentially broken
    supabaseConnected = false;
    
    console.error('Error in executeQuery:', error);
    console.log('Using fallback query execution for:', query);
    
    // For all query types, try the MCP server
    try {
      const result = await executeMcpQuery(query);
      console.log('MCP server query successful');
      return result;
    } catch (mcpError) {
      console.error('MCP server query failed:', mcpError);
      
      // If this is specifically an approval status query and both Supabase and MCP fail
      if (query.includes('SELECT') && 
          query.includes('approved_by_advisor') && 
          query.includes('approved_by_company')) {
        
        console.warn('Both Supabase and MCP failed for approval status query. Returning default values.');
        return [{
          approved_by_advisor: false,
          approved_by_company: false,
          approval_date_advisor: null,
          approval_date_company: null
        }];
      }
      
      // For INSERT queries, generate a UUID and provide a basic response
      if (query.toLowerCase().startsWith('insert')) {
        console.warn('Both Supabase and MCP failed for INSERT query. Generating fallback response.');
        return [{
          id: crypto.randomUUID ? crypto.randomUUID() : Date.now().toString(36) + Math.random().toString(36).substring(2),
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        }];
      }
      
      throw mcpError;
    }
  }
};

// Function to reinitialize Supabase client in case of connection issues
export const reinitializeSupabaseClient = () => {
  console.log('Reinitializing Supabase client connection...');
  // Since we're now using the imported client, we can't recreate it here,
  // but we can test the connection to effectively reinitialize the session
  return testSupabaseConnection().then(connected => {
    supabaseConnected = connected;
    return supabase;
  });
};

// Function to test the Supabase connection
export const testSupabaseConnection = async (): Promise<boolean> => {
  try {
    const { data, error } = await supabase.from('applications').select('count(*)', { count: 'exact' }).limit(1);
    if (error) {
      console.error('Supabase connection test failed:', error);
      return false;
    }
    console.log('Supabase connection test successful');
    return true;
  } catch (error) {
    console.error('Supabase connection test error:', error);
    return false;
  }
};

// Export supabase client for direct usage
export { supabase };

/**
 * Escapa caracteres especiales en string para prevenir inyección SQL
 * @param value String a escapar
 * @returns String escapado
 */
export const escapeSqlString = (value: string): string => {
  if (!value) return '';
  
  // Reemplazar comillas simples por dos comillas simples (estándar SQL)
  return value.replace(/'/g, "''");
};

/**
 * Formatea una fecha para SQL
 * @param date Fecha a formatear
 * @returns String formateado para SQL
 */
export const formatDateForSql = (date: Date): string => {
  return date.toISOString();
};

/**
 * Convierte un objeto a una cadena de condiciones SQL WHERE
 * @param conditions Objeto con condiciones {columna: valor}
 * @returns String con condiciones SQL
 */
export const objectToSqlWhere = (conditions: Record<string, any>): string => {
  const clauses = [];
  
  for (const [key, value] of Object.entries(conditions)) {
    if (value === undefined || value === null) continue;
    
    if (typeof value === 'string') {
      clauses.push(`${key} = '${escapeSqlString(value)}'`);
    } else if (typeof value === 'number' || typeof value === 'boolean') {
      clauses.push(`${key} = ${value}`);
    } else if (value instanceof Date) {
      clauses.push(`${key} = '${formatDateForSql(value)}'`);
    } else if (Array.isArray(value)) {
      const formattedValues = value.map(v => 
        typeof v === 'string' ? `'${escapeSqlString(v)}'` : v
      );
      clauses.push(`${key} IN (${formattedValues.join(',')})`);
    }
  }
  
  return clauses.length > 0 ? clauses.join(' AND ') : '1=1';
}; 

/**
 * Fetches approval statuses for multiple applications in a single batch query
 * @param ids Array of application IDs to fetch approval statuses for
 * @returns A Map with application IDs as keys and approval data as values
 */
export const fetchApprovalsBatch = async (ids: string[]) => {
  if (!ids || ids.length === 0) {
    return new Map();
  }

  try {
    console.log(`Fetching approval statuses in batch for ${ids.length} applications`);
    
    // Execute a single query for all IDs instead of individual queries
    const { data, error } = await supabase
      .from('applications')
      .select('id, approved_by_advisor, approved_by_company, approval_date_advisor, approval_date_company')
      .in('id', ids);
    
    if (error) {
      console.error('Error fetching approval statuses in batch:', error);
      throw error;
    }
    
    if (!data || data.length === 0) {
      console.warn('No approval data returned for batch query');
      return new Map();
    }
    
    console.log(`Successfully fetched ${data.length} approval statuses in batch`);
    
    // Store results in the cache
    data.forEach(item => {
      const cachedResultKey = `approval_status_${item.id}`;
      try {
        sessionStorage.setItem(cachedResultKey, JSON.stringify(item));
      } catch (e) {
        // Ignore storage errors
      }
    });
    
    // Return a Map for easy lookup by ID
    return new Map(data.map(item => [
      item.id, 
      {
        approvedByAdvisor: item.approved_by_advisor || false,
        approvedByCompany: item.approved_by_company || false,
        approvalDateAdvisor: item.approval_date_advisor,
        approvalDateCompany: item.approval_date_company
      }
    ]));
  } catch (error) {
    console.error('Error in fetchApprovalsBatch:', error);
    // Return empty Map on error
    return new Map();
  }
}

// Initialize connection status check
setTimeout(() => {
  ensureSupabaseConnection();
}, 1000); 